import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePhysicians } from "@/hooks/usePhysicians";
import { Button } from "@paribus/shared-ui";
import { ChevronRight, Pencil, Plus, X } from "lucide-react";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";

import { SpecialtiesRepository, useRepository } from "../../repositories";
import { getErrorMessage } from "../../utils";

const EditableCell = ({ value, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <input
        type="text"
        className="w-full rounded-md border px-2 py-1"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyPress={(e) => e.key === "Enter" && handleSave()}
        autoFocus
      />
    );
  }

  return (
    <div className="flex items-center space-x-2">
      <span>{value}</span>
      <button onClick={() => setIsEditing(true)} className="rounded p-1 hover:bg-surface-sunken">
        <Pencil className="h-4 w-4 text-muted-foreground" />
      </button>
    </div>
  );
};

EditableCell.propTypes = {
  value: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
};

const InvitePhysicianModal = ({ isOpen, onClose, onSubmit, specialties }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    specialty_id: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: "", email: "", specialty_id: "" });
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size="sm" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0 text-left">
          <DialogTitle className="text-lg font-semibold">Invite New Physician</DialogTitle>
          <DialogClose className="rounded p-1 hover:bg-surface-sunken">
            <X className="h-5 w-5" />
          </DialogClose>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground">Name</label>
              <input
                type="text"
                required
                disabled={isSubmitting}
                className="mt-1 block w-full rounded-md border border-input px-3 py-2 disabled:cursor-not-allowed disabled:bg-surface-sunken"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-muted-foreground">Email</label>
              <input
                type="email"
                required
                disabled={isSubmitting}
                className="mt-1 block w-full rounded-md border border-input px-3 py-2 disabled:cursor-not-allowed disabled:bg-surface-sunken"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-muted-foreground">Specialty</label>
              <select
                required
                disabled={isSubmitting}
                className="mt-1 block w-full rounded-md border border-input px-3 py-2 disabled:cursor-not-allowed disabled:bg-surface-sunken"
                value={formData.specialty_id}
                onChange={(e) => setFormData({ ...formData, specialty_id: e.target.value })}
              >
                <option value="">Select Specialty</option>
                {specialties.map((specialty) => (
                  <option key={specialty.specialty_id} value={specialty.specialty_id}>
                    {specialty.specialty_name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm italic text-muted-foreground">
                Sets the specialty template this physician&apos;s preferences inherit from. It can
                be changed later, and their own customizations are kept.
              </p>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-input px-4 py-2 hover:bg-surface-subtle"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md bg-primary px-4 py-2 text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? "Sending..." : "Send Invitation"}
              </button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

InvitePhysicianModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  specialties: PropTypes.array.isRequired,
};

const PhysicianManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const specialtiesRepository = useRepository(SpecialtiesRepository);
  const {
    physicians,
    isLoading: physiciansLoading,
    isError: physiciansError,
    errorMessage: physiciansErrorMessage,
    updatePhysicianName,
    updatePhysicianSpecialty,
    invitePhysician,
    reinvitePhysician,
  } = usePhysicians();
  const [specialties, setSpecialties] = useState([]);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(null);

  useEffect(() => {
    fetchSpecialties();
  }, []);

  useEffect(() => {
    if (!physiciansError) return;
    console.error("Error fetching physicians:", physiciansErrorMessage);
    toast.error(physiciansErrorMessage, { id: "physicians-load-error" });
  }, [physiciansError, physiciansErrorMessage]);

  const fetchSpecialties = async () => {
    try {
      const data = await specialtiesRepository.getFirmSpecialties();
      setSpecialties(data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load specialties");
    }
  };

  const handleNameChange = async (userId, newName) => {
    try {
      await updatePhysicianName(userId, newName);
      toast.success("Name updated");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update name");
    }
  };

  const handleSpecialtyChange = async (userId, specialtyId) => {
    try {
      await updatePhysicianSpecialty(userId, specialtyId);
      toast.success("Specialty updated");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update specialty");
    }
  };

  const handleInviteSubmit = async (formData) => {
    await invitePhysician(formData);
    setIsInviteModalOpen(false);
    toast.success("Invitation sent successfully");
  };

  const handleResendInvite = async (email) => {
    if (!email) {
      toast.error("Cannot resend invitation: email is missing");
      return;
    }
    setResendingEmail(email);
    try {
      const message = await reinvitePhysician(email);
      toast.success(message || "Invitation resent successfully");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setResendingEmail(null);
    }
  };

  const handleEditTemplate = (physician) => {
    navigate(`/templates/${physician.template_id || "new"}`, {
      state: {
        physicianId: physician.user_id,
        physicianName: physician.name,
      },
    });
  };

  const getNavigationLinks = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const links = [];

    if (pathSegments.includes("templates")) {
      const templateId = pathSegments[pathSegments.indexOf("templates") + 1];
      links.push({
        to: `/templates/${templateId}`,
        label: "Review Preferences",
      });
    }

    if (pathSegments.includes("document-types")) {
      const templateId = pathSegments[pathSegments.indexOf("templates") + 1];
      const docType = pathSegments[pathSegments.indexOf("document-types") + 1];
      links.push({
        to: `/templates/${templateId}/document-types/${docType}`,
        label: "Billing Report",
      });
    }

    return links;
  };

  if (physiciansLoading) {
    return <div className="flex h-64 items-center justify-center">Loading...</div>;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-4 text-sm text-muted-foreground">
        {getNavigationLinks().map((link, index) => (
          <React.Fragment key={link.to}>
            {index > 0 && <span className="mx-2">&gt;</span>}
            <button onClick={() => navigate(link.to)} className="hover:text-primary">
              {link.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="rounded-lg bg-card shadow">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-xl font-semibold text-foreground">Physician Management</h2>
          <button
            onClick={() => setIsInviteModalOpen(true)}
            className="flex items-center space-x-2 rounded-md bg-primary px-4 py-2 text-white hover:bg-primary-hover"
          >
            <Plus className="h-4 w-4" />
            <span>Invite Physician</span>
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-subtle">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-micro text-muted-foreground"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-micro text-muted-foreground"
                >
                  Email
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-micro text-muted-foreground"
                >
                  Specialty
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-micro text-muted-foreground"
                >
                  Template
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {physicians.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-muted-foreground">
                    {physiciansError ? (
                      <>Couldn&apos;t load physicians. Refresh the page to try again.</>
                    ) : (
                      <>
                        No physicians found. Click &quot;Invite Physician&quot; to invite a
                        physician to your firm.
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                physicians.map((physician) => (
                  <tr key={physician.user_id}>
                    <td className="whitespace-nowrap px-6 py-4">
                      <EditableCell
                        value={physician.name}
                        onSave={(newName) => handleNameChange(physician.user_id, newName)}
                      />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-muted-foreground">
                      {physician.email}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <select
                        className="w-[200px] rounded-md border px-2 py-1"
                        value={physician.specialty_id || ""}
                        onChange={(e) => handleSpecialtyChange(physician.user_id, e.target.value)}
                      >
                        <option value="">Select Specialty</option>
                        {specialties.map((specialty) => (
                          <option key={specialty.specialty_id} value={specialty.specialty_id}>
                            {specialty.specialty_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="secondary"
                          className="w-[160px] justify-between bg-primary-subtle text-primary-emphasis hover:bg-primary-subtle-hover hover:text-primary-emphasis active:bg-primary-subtle-hover"
                          trailingIcon={<ChevronRight />}
                          onClick={() => handleEditTemplate(physician)}
                        >
                          {physician.template_id ? "Edit Template" : "Create Template"}
                        </Button>
                        {physician.invite_pending && physician.email && (
                          <Button
                            variant="outline"
                            size="sm"
                            loading={resendingEmail === physician.email}
                            onClick={() => handleResendInvite(physician.email)}
                          >
                            Resend
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <InvitePhysicianModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSubmit={handleInviteSubmit}
        specialties={specialties}
      />
    </div>
  );
};

export default PhysicianManagement;
